import { useState } from "react";
import { useHistory } from "react-router-dom";
import { Col, Row } from "react-bootstrap";

import Card from "components/core/basic/Card";
import WalmartMarketplaceSetup from "./integrations/WalmartMarketplaceSetup";
import WalmartAdvertisingSetup from "./integrations/WalmartAdvertisingSetup";
import ShopifySetup from "./integrations/ShopifySetup";
import { Button } from "../components/core/basic/Button";
import { useAppsStatus } from "../modules/admin/api/useAppsStatus";
import Loading from "../components/core/blocks/Loading";
import { PageContainer } from "../components/core/basic/PageContainer";

const Apps = () => {
  const appsStatusQuery = useAppsStatus({ refetchInterval: 30000 });
  const [showWMMPSetup, setShowWMMPSetup] = useState(false);
  const [showWalmartAdvertiserSetup, setShowWalmartAdvertiserSetup] = useState(false);
  const [showShopifySetup, setShowShopifySetup] = useState(false);

  const history = useHistory();

  const getStatusText = (status: string) => {
    switch (status) {
      case "not_connected":
        return "Not Connected";
      case "connected":
        return "Connected";
      case "loading":
        return "Loading...";
      case "not_a_google_org":
        return "Select a google organization to see preferences.";
      default:
        return "Waiting Approval";
    }
  };

  if (appsStatusQuery.isLoading) {
    return <Loading fullPage />;
  }

  if (!appsStatusQuery.isSuccess) {
    return <div>Failed to load apps status</div>;
  }

  return (
    <>
      <PageContainer paddingX="xxlarge">
        <Row>
          <Col className={"s8 offset-s2 text-center"}>
            <h4>
              <b>Trellis Connect Apps</b>
            </h4>
          </Col>
        </Row>

        <Row>
          <Col>
            <Card>
              <Row style={{ margin: "20px" }} className="fs-4">
                <Col xs={12}>
                  <p className="text-center" style={{ marginBottom: "50px" }}>
                    On the Trellis Connect Apps screen you can enable various apps to unlock new features.
                  </p>
                  <Row style={{ paddingBottom: "15px" }}>
                    <Col xs={6}>
                      <strong>App</strong>
                    </Col>
                    <Col className={"text-center"} xs={3}>
                      <strong>Connect</strong>
                    </Col>
                    <Col xs={3}>&nbsp;</Col>
                  </Row>
                  <Row
                    style={{
                      paddingBottom: "10px",
                      marginBottom: "10px",
                      borderBottom: "2px solid #ddd",
                    }}
                  >
                    <Col style={{ paddingTop: "10px" }} xs={6}>
                      Amazon Advertising
                    </Col>
                    <Col className={"text-center"} style={{ paddingTop: "10px" }} xs={3}>
                      {getStatusText(appsStatusQuery.data.amazon.status)}
                    </Col>
                    <Col className={"text-center"} xs={3}>
                      <Button variant={"secondary"} onPress={() => history.push("/user/preferences")}>
                        Preferences
                      </Button>
                    </Col>
                  </Row>
                  <Row
                    style={{
                      paddingBottom: "10px",
                      marginBottom: "10px",
                      borderBottom: "2px solid #ddd",
                    }}
                  >
                    <Col style={{ paddingTop: "10px" }} xs={6}>
                      Amazon Connect
                    </Col>
                    <Col className={"text-center"} style={{ paddingTop: "10px" }} xs={3}>
                      {getStatusText(appsStatusQuery.data.amazon_mws.status)}
                    </Col>
                    <Col className={"text-center"} xs={3}>
                      {appsStatusQuery.data.amazon_mws.status === "connected" ?
                        <Button variant="secondary" onPress={() => history.push("/user/amzsummary")}>
                          Preferences
                        </Button>
                      : <Button onPress={() => history.push("/user/amzsummary")}>Connect</Button>}
                    </Col>
                  </Row>

                  <Row
                    style={{
                      paddingBottom: "10px",
                      marginBottom: "10px",
                      borderBottom: "2px solid #ddd",
                    }}
                  >
                    <Col style={{ paddingTop: "10px" }} xs={6}>
                      Walmart Advertising
                    </Col>
                    <Col className={"text-center"} style={{ paddingTop: "10px" }} xs={3}>
                      {getStatusText(appsStatusQuery.data["wm-advertising"].status)}
                    </Col>
                    <Col className={"text-center"} xs={3}>
                      {showWalmartAdvertiserSetup ?
                        <WalmartAdvertisingSetup
                          open={showWalmartAdvertiserSetup}
                          onClose={() => {
                            void appsStatusQuery.refetch();
                            setShowWalmartAdvertiserSetup(false);
                          }}
                        />
                      : null}
                      <Button onPress={() => setShowWalmartAdvertiserSetup(true)}>Setup</Button>
                    </Col>
                  </Row>

                  <Row
                    style={{
                      paddingBottom: "10px",
                      marginBottom: "10px",
                      borderBottom: "2px solid #ddd",
                    }}
                  >
                    <Col style={{ paddingTop: "10px" }} xs={6}>
                      Walmart Marketplace
                    </Col>
                    <Col className={"text-center"} style={{ paddingTop: "10px" }} xs={3}>
                      {getStatusText(getStatusText(appsStatusQuery.data["wm-mp"].status))}
                    </Col>
                    <Col className={"text-center"} xs={3}>
                      {showWMMPSetup ?
                        // We want to reset the state of this entirely when it closes, hence the conditional.
                        <WalmartMarketplaceSetup
                          open={showWMMPSetup}
                          onClose={() => {
                            void appsStatusQuery.refetch();
                            setShowWMMPSetup(false);
                          }}
                        />
                      : null}
                      <Button onPress={() => setShowWMMPSetup(true)}>Setup</Button>
                    </Col>
                  </Row>
                  <Row
                    style={{
                      paddingBottom: "10px",
                      marginBottom: "10px",
                    }}
                  >
                    <Col style={{ paddingTop: "10px" }} xs={6}>
                      Shopify
                      <div>
                        <small>
                          <a
                            href="https://trellis-website.s3.us-west-2.amazonaws.com/Shopify+Install.pdf"
                            target="_blank"
                            rel="noreferrer"
                          >
                            Instructions on how to setup Shoppify App
                          </a>
                        </small>
                      </div>
                    </Col>
                    <Col className={"text-center"} style={{ paddingTop: "10px" }} xs={3}>
                      {getStatusText(appsStatusQuery.data.shopify.status)}
                    </Col>
                    <Col className={"text-center"} xs={3}>
                      {appsStatusQuery.data.shopify.status === "connected" ?
                        <Button variant={"secondary"} isDisabled>
                          Connected
                        </Button>
                      : <Button onPress={() => setShowShopifySetup(true)}>Connect</Button>}
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      </PageContainer>
      <ShopifySetup is_visible={showShopifySetup} onHide={() => setShowShopifySetup(false)} />
    </>
  );
};

export default Apps;
